import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Parse an ISO date (YYYY-MM-DD) as a local date without tz surprises.
function parseISO(iso) {
  if (!iso || typeof iso !== "string") return null;
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

// Short tick label: "Aug 17".
function shortLabel(iso, fallback) {
  const dt = parseISO(iso);
  if (!dt) return fallback || "";
  return `${MONTHS[dt.getMonth()]} ${dt.getDate()}`;
}

// Full human range for the tooltip header: "Aug 17 – Aug 23" (or cross-month).
function rangeLabel(iso, fallback) {
  const start = parseISO(iso);
  if (!start) return fallback || "";
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const s = `${MONTHS[start.getMonth()]} ${start.getDate()}`;
  const e =
    start.getMonth() === end.getMonth()
      ? `${end.getDate()}`
      : `${MONTHS[end.getMonth()]} ${end.getDate()}`;
  return `${s} – ${e}`;
}

const COLORS = {
  published: "#1BA89F",
  scheduled: "#22D3EE",
  drafts: "#7F8AA3",
};
const CAT_LABELS = { published: "Published", scheduled: "Scheduled", drafts: "Drafts" };

function TooltipBox({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const total = (row.published || 0) + (row.scheduled || 0) + (row.drafts || 0);
  return (
    <div
      className="rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] px-3.5 py-2.5 text-xs shadow-xl"
      data-testid="weekly-chart-tooltip"
    >
      <div className="text-[color:var(--text-1)] font-semibold mb-2">{row.range}</div>
      <div className="space-y-1">
        {["published", "scheduled", "drafts"].map((k) => (
          <div key={k} className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full" style={{ background: COLORS[k] }} />
            <span className="text-[color:var(--text-2)]">{CAT_LABELS[k]}</span>
            <span className="ml-auto font-mono-tabular text-[color:var(--text-1)]">{row[k] || 0}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 pt-2 border-t border-[color:var(--border-subtle)] flex items-center gap-2">
        <span className="text-[color:var(--text-3)] uppercase tracking-wider text-[10px]">Total</span>
        <span className="ml-auto font-mono-tabular font-semibold text-[color:var(--text-1)]">{total}</span>
      </div>
    </div>
  );
}

export default function WeeklyChart({ data }) {
  const rows = useMemo(
    () =>
      (data || []).map((d) => ({
        tick: shortLabel(d.iso_start, d.label),
        range: rangeLabel(d.iso_start, d.label),
        published: d.published || 0,
        scheduled: d.scheduled || 0,
        drafts: d.drafts || 0,
      })),
    [data],
  );

  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <div style={{ width: "100%", height: 240 }} data-testid="weekly-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 12, right: 8, left: -12, bottom: 4 }} barCategoryGap="26%">
          <CartesianGrid stroke="#1E2636" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="tick"
            tick={{ fill: "#7F8AA3", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={8}
            tickMargin={8}
          />
          <YAxis
            tick={{ fill: "#7F8AA3", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={30}
            allowDecimals={false}
            domain={[0, (max) => Math.max(4, max)]}
          />
          <Tooltip content={<TooltipBox />} cursor={{ fill: "rgba(34,211,238,0.06)" }} />
          <Bar
            dataKey="published"
            stackId="a"
            fill={COLORS.published}
            isAnimationActive={!prefersReduced}
            animationDuration={650}
          />
          <Bar
            dataKey="scheduled"
            stackId="a"
            fill={COLORS.scheduled}
            isAnimationActive={!prefersReduced}
            animationDuration={650}
          />
          <Bar
            dataKey="drafts"
            stackId="a"
            fill={COLORS.drafts}
            radius={[5, 5, 0, 0]}
            isAnimationActive={!prefersReduced}
            animationDuration={650}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
